import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Calendar, Heart, Flame, Star, Award, MessageSquare } from "lucide-react";
import api from "@/lib/api";
import { LevelBadge, PatreonBadge } from "@/components/UserBadge";
import VideoCard from "@/components/VideoCard";
import { timeAgo } from "@/lib/time";

export default function PublicProfile() {
  const { id } = useParams();
  const [user, setUser] = useState(null);
  const [activity, setActivity] = useState(null);
  const [banner, setBanner] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const u = (await api.get(`/users/${id}`)).data;
        setUser(u);
        setActivity((await api.get(`/users/${id}/activity`)).data);
        if (u.banner_id) {
          const bs = (await api.get("/banners")).data;
          setBanner(bs.find((b) => b.id === u.banner_id));
        }
      } catch (e) { /* not found */ }
    })();
  }, [id]);

  if (!user) return <div className="max-w-5xl mx-auto py-16 px-4 text-zinc-500">User not found.</div>;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="public-profile-page">
      <div className="relative rounded-3xl overflow-hidden mb-6 border border-white/5 bg-[#0e0e0e]">
        <div className="h-40 sm:h-56 bg-gradient-to-r from-[#2a0509] to-[#0e0e0e]">
          {banner && <img src={banner.image_url} alt="" className="w-full h-full object-cover opacity-60" />}
        </div>
        <div className="px-6 sm:px-10 pb-8 -mt-14 relative">
          <div className="w-28 h-28 rounded-2xl bg-gradient-to-br from-[#E50914] to-[#7a0309] flex items-center justify-center text-4xl font-black uppercase shadow-[0_0_30px_rgba(229,9,20,0.4)] border-4 border-[#0a0a0a] overflow-hidden">
            {user.avatar ? <img src={user.avatar} alt="" className="w-full h-full object-cover" /> : (user.name || "?")[0]}
          </div>
          <div className="mt-4 flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h1 className="font-display text-3xl sm:text-4xl font-black">{user.name}</h1>
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                <LevelBadge level={user.level} showBar={false} />
                <span className="text-xs text-zinc-400 inline-flex items-center gap-1.5"><Award className="w-3 h-3 text-[#FF9F0A]" /> {user.xp || 0} XP</span>
                <span className="text-xs text-zinc-400 inline-flex items-center gap-1.5"><Flame className="w-3 h-3 text-[#FF9F0A]" /> {user.login_streak || 0} day streak</span>
                {user.role === "admin" && <span className="text-[10px] font-bold text-[#E50914] uppercase tracking-widest">Admin</span>}
                {user.patreon_tier && <PatreonBadge tier={user.patreon_tier} />}
              </div>
              <div className="mt-2 text-xs text-zinc-500 inline-flex items-center gap-1.5">
                <Calendar className="w-3 h-3" /> Joined {user.created_at ? new Date(user.created_at).toLocaleDateString() : "—"}
              </div>
            </div>
          </div>
        </div>
      </div>

      {activity && (
        <>
          <section className="mb-8">
            <h2 className="font-display text-xl font-black mb-4 flex items-center gap-2"><Heart className="w-4 h-4 text-[#E50914]" /> Liked videos ({activity.liked_videos.length})</h2>
            {activity.liked_videos.length === 0 ? (
              <p className="text-sm text-zinc-500">Nothing yet.</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                {activity.liked_videos.map((v) => <VideoCard key={v.id} video={v} />)}
              </div>
            )}
          </section>
          <section>
            <h2 className="font-display text-xl font-black mb-4 flex items-center gap-2"><MessageSquare className="w-4 h-4 text-[#E50914]" /> Recent comments</h2>
            <div className="space-y-2">
              {activity.recent_comments.map((c) => (
                <div key={c.id} className="glass rounded-xl p-3 text-sm">
                  <div className="flex items-center justify-between text-xs text-zinc-500 mb-1">
                    <Link to={`/videos/${c.video_id}`} className="text-[#E50914] hover:underline">on video {c.video_id.slice(0, 10)}</Link>
                    <span>{timeAgo(c.created_at)}</span>
                  </div>
                  <p className="text-zinc-200">{c.text}</p>
                </div>
              ))}
              {activity.recent_comments.length === 0 && <p className="text-sm text-zinc-500">No comments yet.</p>}
            </div>
          </section>
        </>
      )}
    </div>
  );
}
